import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ControlContainer, FormArray, FormBuilder, FormControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { Customer } from '@klickdata/core/customer';
import { FormHelper } from '@klickdata/core/form';
import { CustomValidatorService } from '@klickdata/core/form/src/validators/custom-validator.service';
import { Resource } from '@klickdata/core/resource';
import { OccasionDate } from '@klickdata/core/resource/src/resource.model';

import { takeUntil, filter } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
@Component({
    selector: 'app-resource-access-and-availability',
    templateUrl: './resource-access-and-availability.component.html',
    styleUrls: ['./resource-access-and-availability.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class ResourceAccessAndAvailabilityComponent extends OnDestroyHandler implements OnInit {
    private _resource: Resource;
    @Input() resourcenLabel: string;
    @Input() set resource(value: Resource) {
        this._resource = value;
        if (this._resource?.id) {
            this.updateForm();
        }
    }
    get resource(): Resource {
        return this._resource;
    }
    @Input() customer: Customer;
    @Output() onEditResourceAccessPermision: EventEmitter<{ permType: string; value: any }> = new EventEmitter<{
        permType: string;
        value: any;
    }>();
    @Output() onUpdateResRecurring: EventEmitter<number> = new EventEmitter<number>();
    public resourceForm: FormGroup;

    constructor(
        protected parentFormDirective: FormGroupDirective,
        private fb: FormBuilder,
        protected validatorService: CustomValidatorService
    ) {
        super();
        this.resourceForm = parentFormDirective.form;
        this.buildForm(this.resourceForm);
    }

    ngOnInit(): void {
        this.resourceForm
            .get('collaboration.enroll_approval_required.metadata.occasion_dates')
            .valueChanges.subscribe((dates) => {
                if (dates.some((date) => date.application_start_date || date.application_end_date)) {
                    this.resourceForm.get('collaboration.enroll_approval_required.status').setValue(true);
                }
            });
    }
    private buildForm(resForm: FormGroup) {
        resForm.addControl('always_available', new FormControl(true));
        resForm.addControl('start_date', new FormControl(''));
        resForm.addControl('end_date', new FormControl(''));
        resForm.addControl(
            'collaboration',
            this.fb.group({
                view: this.fb.group({
                    status: [false],
                    exclude: [false],
                    metadata: this.fb.group({
                        publisher_logo: [],
                        diploma_logos: this.fb.group({
                            logo_1: this.fb.group({
                                media_id: [null],
                                alignment: [null],
                            }),
                            logo_2: this.fb.group({
                                media_id: [null],
                                alignment: [null],
                            }),
                            logo_3: this.fb.group({
                                media_id: [null],
                                alignment: [null],
                            }),
                        }),
                    }),
                }),
                mandatory: this.fb.group({
                    status: [false],
                    exclude: [false],
                }),
                enroll_approval_required: this.fb.group({
                    status: [false],
                    exclude: [false],
                    metadata: this.fb.group(
                        {
                            occasion_dates: this.fb.array([]),
                            occasion_always_open: [true],
                        },
                        { validators: [this.validatorService.occasionDatesValidator] }
                    ),
                }),
                signoff_required: this.fb.group({
                    status: [false],
                    exclude: [false],
                }),
            })
        );
        this.resourceForm
            .get('collaboration.enroll_approval_required.metadata.occasion_always_open')
            .valueChanges.pipe(takeUntil(this.destroy))
            .subscribe((occasionAlwaysOpen) => {
                if (occasionAlwaysOpen) {
                    this.resourceOccasions.clear();
                    this.resourceOccasions.markAsDirty();
                } else {
                    this.addDate();
                }
            });
    }
    private updateForm() {
        if (!!this.resource.collaboration?.enroll_approval_required?.metadata?.occasion_dates?.length) {
            this.resourceOccasions.clear();
            this.resource.collaboration.enroll_approval_required.metadata.occasion_dates.forEach((occasion) =>
                this.addDate(occasion)
            );
            FormHelper.resetForm(this.resourceOccasions);
        }
        if (this.resource.collaboration?.enroll_approval_required?.metadata?.occasion_always_open) {
            this.resourceForm
                .get('collaboration.enroll_approval_required.metadata.occasion_always_open')
                .patchValue(true);
        }
    }
    addDate(occasionDate?: OccasionDate) {
        this.resourceOccasions.push(
            this.fb.group({
                id: [occasionDate ? occasionDate.id : null],
                start_date: [occasionDate ? occasionDate.start_date : ''],
                end_date: [occasionDate ? occasionDate.end_date : ''],
                application_start_date: [occasionDate ? occasionDate.application_start_date : ''],
                application_end_date: [occasionDate ? occasionDate.application_end_date : ''],
            })
        );
        if (!occasionDate) {
            this.resourceOccasions.markAsDirty();
        }
    }

    removeDate(i: number) {
        this.resourceOccasions.removeAt(i);
        this.resourceOccasions.markAsDirty();
    }
    get resourceOccasions(): FormArray {
        return this.resourceForm.get('collaboration.enroll_approval_required.metadata.occasion_dates') as FormArray;
    }
    editResourceAccessPermision(permType: string, value: any) {
        this.onEditResourceAccessPermision.emit({ permType, value });
    }
}
